import React, { useRef, useState, useEffect, useCallback } from 'react';
import './leadershipGeneral.css';
import "@fontsource/poppins/400.css"; // Regular
import "@fontsource/poppins/500.css"; // Medium
import "@fontsource/poppins/600.css"; // Semi-Bold
import "@fontsource/poppins/700.css"; // Bold
import PageCover from '../pages/pageCover';
import FloatingActionButtons from '../../floatingActionButtons/floatingActionButtons';
import { downloadPdf, openPdf } from '../utils/pdfGenerator';
import PageTechnicalInfo from '../pages/pageTechnicalInfo';
import PageResume from '../pages/pageResume';
import PageResultsByEmployee from '../pages/pageResultsByEmployee';
import PageResultsByDimension from '../pages/pageResultsByDimension';
import PageRecommendations from '../pages/pageRecommenations';
import { Switch, Typography } from 'antd';
import PageResultsByOrgItems from '../pages/pageResultsByOrgItems';
import {
    getEvaluationScoreColor,
    isIpraService,
    SELFCARE360_DIMENSION_THRESHOLDS,
} from '../components/selfcare360/selfcare360Scales';
const { Text } = Typography;

const getPersonKey = (person) => [
    person?.id,
    person?.uuid,
    person?.email,
    `${person?.first_name || ''}-${person?.last_name || ''}`,
    `${person?.first_name || ''}-${person?.last_name || ''}-${person?.average ?? ''}`,
].find((value) => value !== undefined && value !== null && value !== '');

const mergeUniquePeople = (...groups) => {
    const seen = new Set();

    return groups.flat().filter((person) => {
        const key = getPersonKey(person);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const LeadershipGeneral = ({ totalResults, individualResults, selectedEvaluation, orgLogo }) => {
    const pdfRef = useRef();
    const [showAll, setShowAll] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState(null);
    let pageNumber = 3;

    const serviceIsIpra = isIpraService(selectedEvaluation?.evl_srv_id);

    const getColor = (value) => getEvaluationScoreColor(value, {
        redLight: selectedEvaluation.evl_max_red_light,
        yellowLight: selectedEvaluation.evl_max_yellow_light,
        serviceId: selectedEvaluation?.evl_srv_id,
    });

    const prepareDimensionData = (dimension, key) => {
        const subdimensions = dimension?.subdimensions || [];
        const greenThreshold = serviceIsIpra
            ? SELFCARE360_DIMENSION_THRESHOLDS.greenMin
            : selectedEvaluation.evl_max_yellow_light;
        const redThreshold = serviceIsIpra
            ? SELFCARE360_DIMENSION_THRESHOLDS.redMax
            : selectedEvaluation.evl_max_red_light;

        const strengths = subdimensions
            .filter(sub => Number(sub.average) >= greenThreshold)
            .sort((a, b) => b.average - a.average)
            .slice(0, 3);

        const gaps = subdimensions
            .filter(sub => Number(sub.average) <= redThreshold)
            .sort((a, b) => a.average - b.average)
            .slice(0, 3);

        let individuals = individualResults
            .map(person => {
                const source = key.toLowerCase().includes("general") ? person.general : person.average_by_dimension || {};
                const match = Object.values(source).find(dim => dim.title === dimension.title);
                if (!match) return null;

                if (key.toLowerCase().includes("general")) {
                    const firstNumeric = match.subdimensions?.find(sub => sub.type === "numeric");
                    if (!firstNumeric) return null;
                    return {
                        first_name: person.first_name,
                        last_name: person.last_name,
                        average: firstNumeric.average
                    };
                }

                return {
                    first_name: person.first_name,
                    last_name: person.last_name,
                    average: match.average
                };
            })
            .filter(Boolean)
            .sort((a, b) => b.average - a.average);

        const recommended = individuals.slice(0, 3);
        const notRecommended = individuals.slice(-3);

        let chartGroups = [];

        if (showAll) {
            let i = 0;
            let firstChunk = true;
            while (i < individuals.length) {
                const size = firstChunk ? 15 : 30;
                chartGroups.push(individuals.slice(i, i + size));
                i += size;
                firstChunk = false;
            }
        } else {
            const best = individuals.slice(0, 5);
            const worst = individuals.slice(-5);
            chartGroups = [mergeUniquePeople(best, worst)];
        }

        return {
            dimension,
            strengths,
            gaps,
            recommended,
            notRecommended,
            chartGroups,
            globalAverage: dimension.average,
            color: getColor(dimension.average),
            type: key.toLowerCase().includes("general") ? 2 : 1
        };
    };

    const preparedDimensions = Object.entries(totalResults.average_by_dimension).map(
        ([key, dimension]) => ({
            key,
            ...prepareDimensionData(dimension, key),
        })
    );

    const pdfFilename = `${selectedEvaluation.evl_title} - Reporte General.pdf`;
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfProgress, setPdfProgress] = useState(null);
    const onProgress = useCallback((current, total) => setPdfProgress({ current, total }), []);
    const handleDownloadPDF = async () => { setPdfLoading(true); await downloadPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };
    const handleOpenPDF = async () => { setPdfLoading(true); await openPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };

    // 1. Define primero chunkArray
    const chunkArray = (array, size) => {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    };

    // 2. Luego define prepareGroupData
    const prepareGroupData = (array, title, subtitle, singularSubtitle) => {
        const sortedArray = [...array].sort((a, b) => b.average - a.average);
        const chunks = chunkArray(sortedArray, 15);

        return {
            title,
            subtitle,
            singularSubtitle,
            chartData: sortedArray,
            chunks
        };
    };

    // Ejemplo de uso:
    const groupedResults = [
        prepareGroupData(totalResults.average_by_job_title, "Resultados generales por cargo", "Los cargos", "El cargo"),
        prepareGroupData(totalResults.average_by_job_category, "Resultados generales por categoría de cargo", "Las categorías de cargo", "La categoría de cargo"),
        prepareGroupData(totalResults.average_by_area, "Resultados generales por área", "Las áreas", "El área"),
        prepareGroupData(totalResults.average_by_management_area, "Resultados generales por gerencia", "Las gerencias", "La gerencia"),
        prepareGroupData(totalResults.average_by_headquarter, "Resultados generales por sede", "Las sedes", "La sede"),
    ];

    return (
        <>
            {/* Control Switch */}
            <div className='sticky-switch' >
                <div className='sticky-switch-content'>
                    <Text style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, }}>
                        Visualización de colaboradores
                    </Text>
                    <Switch
                        checked={showAll}
                        onChange={setShowAll}
                        checkedChildren={<span style={{ fontSize: 14 }}>Todos</span>}
                        unCheckedChildren={<span style={{ fontSize: 14 }}>Puntajes más altos/bajos (5)</span>}
                        // style={{ transform: 'scale(1.2)' }}
                    />
                </div>
            </div>

            {/* Contenido PDF */}
            <div ref={pdfRef} style={{ fontFamily: 'Poppins, sans-serif' }} className="pdf-content">
                <PageCover
                    reportTitle={"REPORTE GENERAL"}
                    reportSubtitle={selectedEvaluation.evl_title}
                    orgName={selectedEvaluation.evl_email_responsible}
                    orgRole={selectedEvaluation.evl_email_responsible_job_title}
                    year={new Date(selectedEvaluation.evl_start_date).getFullYear()}
                    general={true}
                    orgLogo={orgLogo}
                />
                <PageTechnicalInfo selectedEvaluation={selectedEvaluation} totalResults={totalResults} individualResults={individualResults} pageNumber={1} />
                <PageResume selectedEvaluation={selectedEvaluation} totalResults={totalResults} pageNumber={2} />
                <PageResultsByEmployee selectedEvaluation={selectedEvaluation} totalResults={totalResults} individualResults={individualResults} pageNumber={3} />

                {groupedResults.map((group, i) =>
                    group.chunks.map((chunk, j) => {
                        if (chunk.length > 1) {
                            pageNumber++;
                            return (
                                <PageResultsByOrgItems
                                    key={`group-${i}-chunk-${j}`}
                                    selectedEvaluation={selectedEvaluation}
                                    average={totalResults.average}
                                    item={group}
                                    pageNumber={pageNumber}
                                    chartData={chunk}
                                    isFirstPage={j === 0}
                                />
                            );
                        }
                    })
                )}

                {preparedDimensions.map((dimData) => (
                    dimData.chartGroups.map((group, j) => {
                        pageNumber++;
                        return (
                            <PageResultsByDimension
                                key={`${dimData.key}-${j}`}
                                selectedEvaluation={selectedEvaluation}
                                pageNumber={pageNumber}
                                chartData={group}
                                {...dimData}
                                isFirstPage={j === 0}
                            />
                        );
                    })
                ))}

                <PageRecommendations selectedEvaluation={selectedEvaluation} totalResults={totalResults} pageNumber={pageNumber + 1} />
            </div>

            <FloatingActionButtons
                handleDownloadPDF={handleDownloadPDF}
                handleOpenPDF={handleOpenPDF}
                handleExportExcel={() => console.log("Exportando a Excel...")}
                pdfLoading={pdfLoading}
                pdfProgress={pdfProgress}
            />
        </>
    );
};

export default LeadershipGeneral;
